import { Link } from 'react-router'
import { type ReactNode } from 'react'
import type { BadgeColor } from './StatusBadge'

const textClasses: Record<BadgeColor, string> = {
  primary: 'text-link',
  success: 'text-positive',
  danger: 'text-negative',
  warning: 'text-notice',
  info: 'text-informative',
  secondary: 'text-muted',
}

const iconSurfaceClasses: Record<BadgeColor, string> = {
  primary: 'bg-primary-50 text-link',
  success: 'bg-success-50 text-positive',
  danger: 'bg-danger-50 text-negative',
  warning: 'bg-warning-50 text-notice',
  info: 'bg-info-50 text-informative',
  secondary: 'bg-surface-strong text-body',
}

interface DashboardKpiCardProps {
  title: string
  value: number | string | undefined
  icon: ReactNode
  color: BadgeColor
  to: string
  subMetric?: string
  subMetricColor?: BadgeColor
  subMetricIcon?: ReactNode
  footerText?: string
  footerIcon?: ReactNode
  loading?: boolean
}

/**
 * Card KPI per la Dashboard principale: numero neutro, icona grande sbiadita a destra, footer opzionale.
 * Il colore resta solo sull'icona (e sul submetric): il valore principale è neutro per non appesantire
 * la griglia con 8 tinte diverse una accanto all'altra.
 */
export function DashboardKpiCard({
  title, value, icon, color, to,
  subMetric, subMetricColor, subMetricIcon,
  footerText, footerIcon,
  loading = false,
}: DashboardKpiCardProps) {
  return (
    <Link to={to} className="block no-underline" aria-busy={loading}>
      <div className="flex h-full flex-col rounded-xl border border-border bg-canvas shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-md">
        <div className="flex items-center justify-between p-4">
          <div className="min-w-0">
            <h6 className="m-0 mb-1 text-muted">{title}</h6>
            {loading ? (
              <div className="my-1 h-9 w-16 animate-pulse rounded bg-surface-strong" role="status" aria-label={`Caricamento: ${title}`} />
            ) : (
              <h2 className="m-0 text-3xl font-semibold text-heading">{value ?? 0}</h2>
            )}
            {!loading && subMetric && (
              <small className={`flex items-center gap-1 ${textClasses[subMetricColor || 'secondary']}`}>
                {subMetricIcon}
                {subMetric}
              </small>
            )}
          </div>
          <div className={`rounded-xl p-2.5 ${iconSurfaceClasses[color]}`}>{icon}</div>
        </div>
        {loading && footerText ? (
          <div className="mt-auto px-4 pb-4" aria-hidden="true">
            <div className="h-3 w-28 animate-pulse rounded bg-surface-strong" />
          </div>
        ) : footerText ? (
          <div className="mt-auto px-4 pb-4">
            <small className="flex items-center gap-1 text-xs text-muted">
              {footerIcon}
              {footerText}
            </small>
          </div>
        ) : null}
      </div>
    </Link>
  )
}
